// Effort & methodology math — docs/ARCHITECTURE_M0.md §5.5; team-size and
// phase-staffing model (tape-in pipeline) in docs/ARCHITECTURE_M1.md §5.5b.

import type { ChipProject, Discipline, ModelConstants } from "../types.js";

/** World methodology factor M(m): design/verification productivity improves on calendar rails. */
export function methodologyFactor(monthIndex: number, models: ModelConstants): number {
  return Math.pow(models.methodologyGrowthPerYear, monthIndex / 12);
}

export interface ProjectEffortPlan {
  designEmTotal: number;
  implEmTotal: number;
  verifScale: number;
  critLambda: number;
  minorLambda: number;
  blocks: number;
}

/** How many independently-ownable blocks a chip partitions into. Linear in gate
 *  count; tools grow the complexity one block owner can span (era snapshot). */
export function blocksOf(kGates: number, startMonth: number, models: ModelConstants): number {
  const blockKGates = models.blockKGatesBase * Math.pow(methodologyFactor(startMonth, models), models.blockKGatesMethodExp);
  return Math.max(1, kGates / blockKGates);
}

/** Snapshot at project start (methodology is a project-level commitment).
 *  Off-path projects (design §5) cost more everywhere: the team is working
 *  outside its mastery tree, so design/impl effort AND the verification burden
 *  scale by offPathEffortMult (coverage is bought at off-path prices too).
 *  `methodMult` (M3, design §6.3): the EDA-tier flow quality at start — premium
 *  tools are a small standing multiplier on the world methodology curve. */
export function planProjectEffort(
  kGates: number,
  startMonth: number,
  models: ModelConstants,
  offPath = false,
  methodMult = 1,
): ProjectEffortPlan {
  const M = methodologyFactor(startMonth, models) * methodMult;
  const pathMult = offPath ? models.offPathEffortMult : 1;
  const designEmTotal =
    ((models.designEmCoeff * Math.pow(kGates, models.designEmExp)) / Math.pow(M, models.methodologyDesignExp)) *
    pathMult;
  const implEmTotal = models.implEmRatio * designEmTotal;
  // verification productivity outpaces design productivity (its own tooling arc)
  const Mv = Math.pow(models.verifMethodologyExtraPerYear, startMonth / 12);
  const verifScale = (models.verifScaleRatio * designEmTotal) / Mv;
  const critLambda = (models.critLambdaCoeff * Math.pow(kGates, models.critLambdaExp)) / M;
  return {
    designEmTotal,
    implEmTotal,
    verifScale,
    critLambda,
    minorLambda: models.minorLambdaRatio * critLambda,
    blocks: blocksOf(kGates, startMonth, models),
  };
}

/** Player-visible coverage-confidence gauge. Clamped strictly below 1 (invariant:
 *  cov ∈ [0,1)) — without the clamp, exp() underflow makes coverage EXACTLY 1.0
 *  once verifEmDone/verifScale ≳ 37, and certainty must not be purchasable. */
export function coverageOf(verifEmDone: number, verifScale: number): number {
  return Math.min(1 - 1e-9, 1 - Math.exp(-verifEmDone / verifScale));
}

/** Requirements-fit from feasibility effort. */
export function feasibilityFit(feasEmDone: number, models: ModelConstants): number {
  return models.feasFitBase + models.feasFitSpan * (1 - Math.exp(-feasEmDone / models.feasFitTau));
}

/** Company experience velocity factor. */
export function experienceFactor(shippedProjects: number, models: ModelConstants): number {
  return 1 + Math.min(models.expCap, models.expPerShipped * shippedProjects);
}

/** Team throughput as an S-curve in headcount, anchored to the ideal team
 *  (staff-per-block × blocks — see idealStaffing):
 *  - STARVED (low end): below starveKnee×(1−1/blocks) of ideal, per-head output
 *    ramps down toward starveFloor — a skeleton crew on a many-block chip
 *    thrashes across contexts and nothing reaches critical mass. A 1-block chip
 *    never starves: one owner per block is fine.
 *  - LINEAR (middle): from the knee to the ideal, every head is worth 1 — ten
 *    people on ten blocks is ten blocks moving.
 *  - DIE-OFF (high end): past the ideal, marginal value decays exponentially
 *    toward a ceiling of (1 + teamOverflowFrac) × ideal — the eleventh person
 *    on a ten-block chip has no block to own. */
export function effectiveHeads(n: number, nIdeal: number, blocks: number, models: ModelConstants): number {
  if (!(nIdeal > 0) || n <= 0) return 0;
  if (n > nIdeal) {
    const k = models.teamOverflowFrac * nIdeal;
    if (k <= 0) return nIdeal;
    return nIdeal + k * (1 - Math.exp(-(n - nIdeal) / k));
  }
  const knee = models.starveKnee * (1 - 1 / Math.max(1, blocks)) * nIdeal;
  if (n >= knee || knee <= 0) return n;
  // smoothstep ramp from starveFloor toward 1 at the knee (C¹ at the knee)
  const t = n / knee;
  const s = t * t * (3 - 2 * t);
  return n * (models.starveFloor + (1 - models.starveFloor) * s);
}

/** Ideal (full-productivity) team size per discipline for the project's CURRENT
 *  phase. Parallelism is set by the block count: ideal = staff-per-block × blocks,
 *  so ideal teams grow LINEARLY with gate count and tools grow the block size.
 *  - design phase:   designers at full ideal; a small physical-design advance
 *    party (floorplan/synthesis trials); verification steady from day one.
 *  - layout phase:   implementation at full ideal; a design ECO/support crew;
 *    verification steady.
 *  - waiting phases: verification (plus the pre-tape-in advance party).
 *  Zero means the discipline has nothing useful to do in that phase. */
export function idealStaffing(
  project: Pick<ChipProject, "phase" | "blocks">,
  models: ModelConstants,
): Record<Discipline, number> {
  const designIdeal = models.designersPerBlock * project.blocks;
  const implIdeal = models.implPerBlock * project.blocks;
  const verifIdeal = models.verifPerBlock * project.blocks;
  switch (project.phase) {
    case "feasibility":
      return { design: models.feasIdealStaff, verification: 0, implementation: 0 };
    case "design":
      return {
        design: designIdeal,
        verification: verifIdeal,
        implementation: models.designPhaseImplFrac * implIdeal,
      };
    case "waitingTapein":
      // the physical advance party can keep working while management decides
      return { design: 0, verification: verifIdeal, implementation: models.designPhaseImplFrac * implIdeal };
    case "layout":
      return {
        design: models.layoutDesignSupportFrac * designIdeal,
        verification: verifIdeal,
        implementation: implIdeal,
      };
    case "waitingTapeout":
      return { design: 0, verification: verifIdeal, implementation: 0 };
    case "respinFix":
      return { design: models.respinFixIdealStaff, verification: 0, implementation: 0 };
    case "prodQual":
      return { design: 0, verification: 0, implementation: models.prodQualMinImpl };
    default:
      return { design: 0, verification: 0, implementation: 0 };
  }
}
